// Per-site RAG knowledge base client. Mirrors commands-api.ts: typed thin
// wrappers over apiFetch hitting the super-admin / site-admin CRUD router at
// /api/admin/sites/:siteId/knowledge.
//
// Create/update re-embed the entry through the embeddings gateway, which can be
// down — the server then answers 503 `embeddings-unavailable` or 502
// `embeddings-failed` with a human `message`. `embeddingsErrorMessage()` pulls
// that message out of a thrown ApiError so the UI can show the admin exactly
// what to fix (usually the embeddings key), rather than a generic failure.

import { apiFetch, isApiError } from './api';

/** text/md/faq/product are typed (or imported) by an admin; pdf is an
 *  uploaded file's extracted text; url is a crawled page, owned by its
 *  website source. */
export type KnowledgeKind = 'text' | 'md' | 'faq' | 'pdf' | 'url' | 'product';

export interface KnowledgeEntry {
  id: string;
  siteId: string;
  kind: KnowledgeKind;
  title: string;
  content: string;
  /** Set for crawled pages: the website source that owns this entry. */
  sourceId: string | null;
  sourceUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeInput {
  /** 'url' only when editing a crawled page (it then leaves its website). */
  kind: KnowledgeKind;
  title: string;
  content: string;
  /** A product's page; null clears it. */
  sourceUrl?: string | null;
}

export type CrawlStatus = 'queued' | 'crawling' | 'done' | 'failed';

/** A website crawled into the knowledge base; its pages are entries. */
export interface KnowledgeSource {
  id: string;
  siteId: string;
  url: string;
  maxPages: number;
  /** 'site' crawls the website; 'page' reads only this address. */
  mode: 'site' | 'page';
  status: CrawlStatus;
  pagesFetched: number;
  pagesIndexed: number;
  pagesFailed: number;
  /** First few problems (unreachable pages, early stop), newest crawl only. */
  errors: string[];
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/** List a site's knowledge entries (API sorts newest-first). */
export async function listKnowledge(siteId: string): Promise<KnowledgeEntry[]> {
  const { entries } = await apiFetch<{ entries: KnowledgeEntry[] }>(
    `/api/admin/sites/${siteId}/knowledge`,
  );
  return entries;
}

export async function createKnowledge(
  siteId: string,
  input: KnowledgeInput,
): Promise<KnowledgeEntry> {
  const { entry } = await apiFetch<{ entry: KnowledgeEntry }>(
    `/api/admin/sites/${siteId}/knowledge`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return entry;
}

export async function updateKnowledge(
  siteId: string,
  entryId: string,
  input: KnowledgeInput,
): Promise<KnowledgeEntry> {
  const { entry } = await apiFetch<{ entry: KnowledgeEntry }>(
    `/api/admin/sites/${siteId}/knowledge/${entryId}`,
    { method: 'PUT', body: JSON.stringify(input) },
  );
  return entry;
}

export async function deleteKnowledge(siteId: string, entryId: string): Promise<void> {
  await apiFetch(`/api/admin/sites/${siteId}/knowledge/${entryId}`, { method: 'DELETE' });
}

/** Upload a PDF; the server extracts its text into one entry. */
export async function uploadKnowledgePdf(siteId: string, file: File): Promise<KnowledgeEntry> {
  const { entry } = await apiFetch<{ entry: KnowledgeEntry }>(
    `/api/admin/sites/${siteId}/knowledge/pdf?filename=${encodeURIComponent(file.name)}`,
    { method: 'POST', body: file, headers: { 'Content-Type': 'application/pdf' } },
  );
  return entry;
}

export async function listKnowledgeSources(siteId: string): Promise<KnowledgeSource[]> {
  const { sources } = await apiFetch<{ sources: KnowledgeSource[] }>(
    `/api/admin/sites/${siteId}/knowledge/sources`,
  );
  return sources;
}

/** Add a website (or one page of it) and start reading it; the crawl runs in
 *  the background. */
export async function addKnowledgeSource(
  siteId: string,
  url: string,
  maxPages: number,
  mode: 'site' | 'page' = 'site',
): Promise<KnowledgeSource> {
  const { source } = await apiFetch<{ source: KnowledgeSource }>(
    `/api/admin/sites/${siteId}/knowledge/sources`,
    { method: 'POST', body: JSON.stringify({ url, maxPages, mode }) },
  );
  return source;
}

export interface CsvImportResult {
  /** Entries created (Q&A: one per 500 pairs; products: one each). */
  created: number;
  /** Rows imported. */
  items: number;
  skipped: Array<{ row: number; reason: string }>;
}

/** Import a CSV of question/answer pairs or products. */
export async function importKnowledgeCsv(
  siteId: string,
  file: File,
  type: 'faq' | 'products',
): Promise<CsvImportResult> {
  return apiFetch<CsvImportResult>(
    `/api/admin/sites/${siteId}/knowledge/import?type=${type}&filename=${encodeURIComponent(file.name)}`,
    { method: 'POST', body: await file.text(), headers: { 'Content-Type': 'text/csv' } },
  );
}

export async function recrawlKnowledgeSource(siteId: string, sourceId: string): Promise<void> {
  await apiFetch(`/api/admin/sites/${siteId}/knowledge/sources/${sourceId}/crawl`, { method: 'POST' });
}

export async function deleteKnowledgeSource(siteId: string, sourceId: string): Promise<void> {
  await apiFetch(`/api/admin/sites/${siteId}/knowledge/sources/${sourceId}`, { method: 'DELETE' });
}

/** The server's human-readable `message`, when an error carries one. */
export function knowledgeErrorMessage(err: unknown): string | null {
  if (!isApiError(err)) return null;
  const body = err.body as { message?: string } | null;
  return body?.message ?? null;
}

/**
 * If `err` is a 502/503 embeddings-gateway failure from a create/update, return
 * the server's human-readable `message` (falling back to a sensible default);
 * otherwise return null so the caller can fall back to generic error copy.
 */
export function embeddingsErrorMessage(err: unknown): string | null {
  if (!isApiError(err)) return null;
  if (err.status !== 502 && err.status !== 503) return null;
  const body = err.body as { error?: string; message?: string } | null;
  if (body?.error !== 'embeddings-unavailable' && body?.error !== 'embeddings-failed') return null;
  return (
    body.message ||
    'The embeddings service is unavailable, so this entry could not be indexed. Check the embeddings API key and try again.'
  );
}

/** AI-drafted questions and answers from one entry, for the admin to review
 *  and save as a Q&A entry (nothing is stored yet). */
export async function generateEntryQa(
  siteId: string,
  entryId: string,
): Promise<{ pairs: Array<{ question: string; answer: string }>; title: string; sourceUrl: string | null }> {
  return apiFetch(`/api/admin/sites/${siteId}/knowledge/${entryId}/generate-qa`, { method: 'POST' });
}

/** Queue Q&A for every page of a website; each becomes a "Q&A: …" entry. */
export async function generateSourceQa(siteId: string, sourceId: string): Promise<number> {
  const { queued } = await apiFetch<{ queued: number }>(
    `/api/admin/sites/${siteId}/knowledge/sources/${sourceId}/generate-qa`,
    { method: 'POST' },
  );
  return queued;
}
